import React, { useState, useRef, useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from 'react-bootstrap'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { signInWithEmailAndPassword, GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth'
import { auth } from '../../../../firebase'
import { generatePopup } from '../../../../utils/popup'
import { PasswordMeterComponent } from '../../../../_4threal/assets/ts/components'
import { useDispatch } from 'react-redux'
import { getCards } from '../../../../redux/action/card'
import ReactGA from "react-ga4"

// Step definitions
enum SignupStep {
  EMAIL = 'email',
  VERIFY_CODE = 'verify_code',
  COMPLETE_PROFILE = 'complete_profile',
  SUCCESS = 'success'
}

// MFA Style Code Input Component
interface CodeInputProps {
  length: number
  value: string
  onChange: (value: string) => void
  onComplete: (value: string) => void
  disabled?: boolean
  error?: boolean
}

const CodeInput: React.FC<CodeInputProps> = ({ 
  length, 
  value, 
  onChange, 
  onComplete, 
  disabled = false,
  error = false 
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (value.length === length) {
      onComplete(value)
    }
  }, [value, length, onComplete])

  const handleChange = (index: number, newValue: string) => {
    // Only allow digits
    const digit = newValue.replace(/\D/g, '')
    
    if (digit.length <= 1) {
      const newCode = value.split('')
      newCode[index] = digit
      const updatedCode = newCode.join('').slice(0, length)
      onChange(updatedCode)

      // Move to next input if digit was entered
      if (digit && index < length - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    onChange(pastedData)
    
    // Focus on the next empty input or the last one
    const nextIndex = Math.min(pastedData.length, length - 1)
    inputRefs.current[nextIndex]?.focus()
  }

  return (
    <div className="d-flex justify-content-center gap-3 mb-4">
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          className={clsx(
            'form-control text-center fs-2 fw-bold',
            {
              'border-danger': error,
              'border-success': value.length === length && !error,
              'border-primary': value[index] && !error && value.length !== length
            }
          )}
          style={{
            width: '60px',
            height: '60px',
            fontSize: '24px',
            fontWeight: 'bold'
          }}
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          autoComplete="off"
        />
      ))}
    </div>
  )
}

const MultiStepSignup: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<SignupStep>(SignupStep.EMAIL)
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [sessionId, setSessionId] = useState('')
  const [verifiedToken, setVerifiedToken] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [countdown, setCountdown] = useState(0)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const functions = getFunctions()
  const environment = process.env.REACT_APP_ENVIRONMENT
  const [searchParams] = useSearchParams()
  const affiliateId = searchParams.get('aff')

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [countdown])

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [currentStep])

 
  // Email validation schema
  const emailSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email format').required('Email is required'),
  })

  // Profile completion schema
  const profileSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, 'Minimum 2 characters')
      .max(50, 'Maximum 50 characters')
      .required('First name is required'),
    lastName: Yup.string()
      .min(2, 'Minimum 2 characters')
      .max(50, 'Maximum 50 characters')
      .required('Last name is required'),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters long'),
    confirmPassword: Yup.string()
      .required('Password confirmation is required')
      .oneOf([Yup.ref('password')], "Passwords don't match"),
    acceptTerms: Yup.boolean().required('You must accept the terms and conditions'),
  })

  // Email form
  const emailFormik = useFormik({
    initialValues: { email: '' },
    validationSchema: emailSchema,
    onSubmit: async (values) => {
      await handleEmailSubmit(values.email)
    },
  })

  // Profile form
  const profileFormik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
    },
    validationSchema: profileSchema,
    onSubmit: async (values) => {
      await handleProfileSubmit(values)
    },
  })

  const handleEmailSubmit = async (emailValue: string) => {
    try {
      setLoading(true)
      setEmail(emailValue)
      
      const generateCode = httpsCallable(functions, 'generateSignupVerificationCode')
      const result: any = await generateCode({ email: emailValue })
      
      if (result.data.success) {
        setSessionId(result.data.sessionId)
        setCurrentStep(SignupStep.VERIFY_CODE)
        setCountdown(600) // 10 minutes
        generatePopup('success', 'Verification code sent to your email!')
      }
    } catch (error: any) {
      generatePopup('error', error.message || 'Failed to send verification code')
    } finally {
      setLoading(false)
    }
  }

  const handleCodeComplete = async (code: string) => {
    try {
      setLoading(true)
      setCodeError('')
      
      const validateCode = httpsCallable(functions, 'validateSignupVerificationCode')
      const result: any = await validateCode({ 
        email, 
        code, 
        sessionId 
      })
      
      if (result.data.success) {
        setVerifiedToken(result.data.verifiedToken)
        setCurrentStep(SignupStep.COMPLETE_PROFILE)
        generatePopup('success', 'Email verified! Please complete your profile.')
      }
    } catch (error: any) {
      setCodeError(error.message || 'Invalid verification code')
      setVerificationCode('')
    } finally {
      setLoading(false)
    }
  }

  const handleProfileSubmit = async (values: any) => {
    try {
      setLoading(true)
      
      // Set affiliate metadata BEFORE creating the user account
      const storedAffiliateId = localStorage.getItem('affiliateId')
      const signupWithVerified = httpsCallable(functions, 'signupWithVerifiedEmail')
      const result: any = await signupWithVerified({
        email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        verifiedToken,
      })
      
      if (result.data.success) {
        // Sign in the user
        await signInWithEmailAndPassword(auth, email, values.password)
        
        // Store user ID
        localStorage.setItem('userId', result.data.uid)
        

        // Track conversion
        if (environment === 'production') {
          ReactGA.gtag("event", "conversion", {
            send_to: "AW-16851717275/lzeeCNiz4LwaEJuZw-M-"
          })
          
          // Track Meta Pixel StartTrial event for new user signups
          const win = window as any
          if ('fbq' in win) {
            win.fbq('track', 'StartTrial', {
              value: '0.00',
              currency: 'USD',
              predicted_ltv: '0.00',
            })
          }
        }
        
        // Get user's cards and navigate
        await dispatch(getCards(50, 1, result.data.uid))
        setCurrentStep(SignupStep.SUCCESS)
        const storedAffiliateId = localStorage.getItem('affiliateId')
      if (storedAffiliateId) {
        try {
          const setAffiliateForUserFn = httpsCallable(functions, 'setAffiliateForUser')
          await setAffiliateForUserFn({
            affiliateId: storedAffiliateId,
            userId:result.data.uid
          })
          console.log('Affiliate ID linked via custom claim:', storedAffiliateId)
          localStorage.removeItem('affiliateId')
        } catch (error) {
          console.error('Error setting affiliate for user:', error)
        }
      }
        
          navigate('/dashboard')
      
      }
    } catch (error: any) {
      generatePopup('error', error.message || 'Failed to create account')
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = async () => {
    await handleEmailSubmit(email)
  }

  const handleGoogleSignIn = async () => {
    setLoading(true)
    const provider = new GoogleAuthProvider()
    try {
      const result = await signInWithPopup(auth, provider)
      const user = result.user
      localStorage.setItem('userId', user.uid)
      
      // For social logins, set affiliate ID immediately after user creation
      
      
      // Track conversion for production
      if (environment === 'production') {
        ReactGA.gtag("event", "conversion", {
          send_to: "AW-16851717275/lzeeCNiz4LwaEJuZw-M-"
        })
        
        const win = window as any
        if ('fbq' in win) {
          win.fbq('track', 'StartTrial', {
            value: '0.00',
            currency: 'USD',
            predicted_ltv: '0.00',
          })
        }
      }
      
      await dispatch(getCards(50, 1, user.uid))
      const storedAffiliateId = localStorage.getItem('affiliateId')
      if (storedAffiliateId) {
        try {
          const setAffiliateForUserFn = httpsCallable(functions, 'setAffiliateForUser')
          await setAffiliateForUserFn({
            affiliateId: storedAffiliateId,
            userId: user.uid
          })
          console.log('Affiliate ID linked via custom claim:', storedAffiliateId)
          localStorage.removeItem('affiliateId')
        } catch (error) {
          console.error('Error setting affiliate for user:', error)
        }
      }
      navigate('/dashboard')
    } catch (error: any) {
      console.error('Error during Google sign-in', error)
      
      if (error.code === 'auth/account-exists-with-different-credential') {
        console.log('Account exists with different credential:', error.email)
        generatePopup('info', 'Account linked successfully! You can now sign in with either method.')
      } else {
        generatePopup('error', 'Failed to sign up with Google. Please try again.')
      }
    }
    setLoading(false)
  }

  const handleAppleSignIn = async () => {
    setLoading(true)
    const provider = new OAuthProvider('apple.com')
    provider.addScope('email')
    provider.addScope('name')
    try {
      const result = await signInWithPopup(auth, provider)
      const user = result.user
      localStorage.setItem('userId', user.uid)
      
      // For social logins, set affiliate ID immediately after user creation
    
      
      // Track conversion for production
      if (environment === 'production') {
        ReactGA.gtag("event", "conversion", {
          send_to: "AW-16851717275/lzeeCNiz4LwaEJuZw-M-"
        })
        
        const win = window as any
        if ('fbq' in win) {
          win.fbq('track', 'StartTrial', {
            value: '0.00',
            currency: 'USD',
            predicted_ltv: '0.00',
          })
        }
      }
      
      await dispatch(getCards(50, 1, user.uid))
      const storedAffiliateId = localStorage.getItem('affiliateId')
      if (storedAffiliateId) {
        try {
          const setAffiliateForUserFn = httpsCallable(functions, 'setAffiliateForUser')
          await setAffiliateForUserFn({
            affiliateId: storedAffiliateId,
            userId: user.uid
          })
          console.log('Affiliate ID linked via custom claim:', storedAffiliateId)
          localStorage.removeItem('affiliateId')
        } catch (error) {
          console.error('Error setting affiliate for user:', error)
        }
      }
      navigate('/dashboard')
    } catch (error: any) {
      console.error('Error during Apple sign-in', error)
      
      if (error.code === 'auth/account-exists-with-different-credential') {
        console.log('Account exists with different credential:', error.email)
        generatePopup('info', 'Account linked successfully! You can now sign in with either method.')
      } else {
        generatePopup('error', 'Failed to sign up with Apple. Please try again.')
      }
    }
    setLoading(false)
  }

  const bgTransparent = { background: 'transparent' }

  const renderStep = () => {
    switch (currentStep) {
      case SignupStep.EMAIL:
        return (
          <div className="text-center">
            <h1 className="text-dark fw-bolder mb-3">Get Started</h1>
            <p className="font-dark1 mb-8">Welcome to Skanteq! Let's create your account.</p>
            
            <form onSubmit={emailFormik.handleSubmit} className="w-100">
              <div className="fv-row mb-8">
                <label className="form-label fw-semibold font-dark1 fs-6">Email address</label>
                <input
                  placeholder="Enter your email"
                  type="email"
                  style={bgTransparent}
                  autoComplete="email"
                  {...emailFormik.getFieldProps('email')}
                  className={clsx(
                    'form-control bg-transparent',
                    {
                      'border-danger': emailFormik.touched.email && emailFormik.errors.email,
                    },
                    {
                      'border-success': emailFormik.touched.email && !emailFormik.errors.email,
                    }
                  )}
                />
                {emailFormik.touched.email && emailFormik.errors.email && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{emailFormik.errors.email}</span>
                    </div>
                  </div>
                )}
              </div>
              
              <Button
                type="submit"
                className="btn btn-primary w-100 mb-3"
                disabled={loading || !emailFormik.isValid || !emailFormik.dirty}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Sending verification code...
                  </>
                ) : (
                  'Sign up with Email'
                )}
              </Button>
              
              <div className='separator separator-content my-4'>
                <span className='w-125px text-gray-500 fw-semibold fs-7'>Or</span>
              </div>

              <Button
                type='button'
                className='btn btn-flex flex-center btn-light w-100 mb-3 border border-dark text-dark'
                onClick={handleGoogleSignIn}
                disabled={loading}
                style={{ backgroundColor: 'white' }}
              >
                <img alt='Logo' src='/media/icons/duotune/general/google.svg' className='h-20px me-3' />
                Sign up with Google
              </Button>

              <Button
                type='button'
                className='btn btn-flex flex-center btn-light w-100 mb-3 border border-dark text-dark'
                onClick={handleAppleSignIn}
                disabled={loading}
                style={{ backgroundColor: 'white' }}
              >
                <img alt='Logo' src='/media/icons/duotune/general/apple.svg' className='h-20px me-3' />
                Sign up with Apple
              </Button>
            </form>
          </div>
        )

      case SignupStep.VERIFY_CODE:
        return (
          <div className="text-center">
            <h1 className="text-dark fw-bolder mb-3">Check Your Email</h1>
            <p className="font-dark1 mb-8">
              We sent a 6-digit verification code to<br />
              <strong>{email}</strong>
            </p>
            <p className='font-dark1 text-center mt-4'>Can’t find the code? Check your spam folder!</p>
            
            <CodeInput
              length={6}
              value={verificationCode}
              onChange={setVerificationCode}
              onComplete={handleCodeComplete}
              disabled={loading}
              error={!!codeError}
            />
            
            {codeError && (
              <div className="alert alert-danger mb-4" role="alert">
                {codeError}
              </div>
            )}
            
            <div className="d-flex justify-content-between align-items-center">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setCurrentStep(SignupStep.EMAIL)}
                disabled={loading}
              >
                ← Change Email
              </button>
              
              <button
                type="button"
                className="btn btn-link"
                onClick={handleResendCode}
                disabled={loading || countdown > 0}
              >
                {countdown > 0 
                  ? `Resend in ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')}`
                  : 'Resend Code'}
              </button>
            </div>
          </div>
        )

      case SignupStep.COMPLETE_PROFILE:
        return (
          <div className="text-center">
            <h1 className="text-dark fw-bolder mb-3">Complete Your Profile</h1>
            <p className="font-dark1 mb-8">Just a few more details to get you started!</p>
            
            <form onSubmit={profileFormik.handleSubmit} className="w-100">
              <div className="row mb-4">
                <div className="col-12 col-md-6 fv-row mb-4">
                  <label className="form-label fw-semibold font-dark1 fs-6">First name</label>
                  <input
                    placeholder="First name"
                    type="text"
                    style={bgTransparent}
                    autoComplete="given-name"
                    {...profileFormik.getFieldProps('firstName')}
                    className={clsx(
                      'form-control bg-transparent',
                      {
                        'border-danger': profileFormik.touched.firstName && profileFormik.errors.firstName,
                      },
                      {
                        'border-success': profileFormik.touched.firstName && !profileFormik.errors.firstName,
                      }
                    )}
                  />
                  {profileFormik.touched.firstName && profileFormik.errors.firstName && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <span role="alert">{profileFormik.errors.firstName}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="col-12 col-md-6 fv-row mb-4">
                  <label className="form-label fw-semibold font-dark1 fs-6">Last name</label>
                  <input
                    placeholder="Last name"
                    type="text"
                    style={bgTransparent}
                    autoComplete="family-name"
                    {...profileFormik.getFieldProps('lastName')}
                    className={clsx(
                      'form-control bg-transparent',
                      {
                        'border-danger': profileFormik.touched.lastName && profileFormik.errors.lastName,
                      },
                      {
                        'border-success': profileFormik.touched.lastName && !profileFormik.errors.lastName,
                      }
                    )}
                  />
                  {profileFormik.touched.lastName && profileFormik.errors.lastName && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <span role="alert">{profileFormik.errors.lastName}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="fv-row mb-4" data-kt-password-meter="true">
                <label className="form-label fw-semibold font-dark1 fs-6">Password</label>
                <div className="position-relative mb-3">
                  <input
                    type="password"
                    style={bgTransparent}
                    placeholder="Password"
                    autoComplete="new-password"
                    {...profileFormik.getFieldProps('password')}
                    className={clsx(
                      'form-control bg-transparent',
                      {
                        'border-danger': profileFormik.touched.password && profileFormik.errors.password,
                      },
                      {
                        'border-success': profileFormik.touched.password && !profileFormik.errors.password,
                      }
                    )}
                  />
                  <div
                    style={{ top: '50%', right: '10px', width: '50px', transform: 'translateY(-50%)' }}
                    className="d-flex align-items-center mb-3 position-absolute"
                    data-kt-password-meter-control="highlight"
                  >
                    <div style={{ flexGrow: '0.2' }} className="bg-secondary rounded h-20px me-2"></div>
                    <div style={{ flexGrow: '0.2' }} className="bg-secondary rounded h-20px me-2"></div>
                    <div style={{ flexGrow: '0.2' }} className="bg-secondary rounded h-20px me-2"></div>
                    <div style={{ flexGrow: '0.2' }} className="bg-secondary rounded h-20px"></div>
                  </div>
                </div>
                {profileFormik.touched.password && profileFormik.errors.password && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{profileFormik.errors.password}</span>
                    </div>
                  </div>
                )}
                <div className="text-muted font-gray1">
                  Use 8 or more characters with a mix of letters, numbers & symbols
                </div>
              </div>
              
              <div className="fv-row mb-4">
                <label className="form-label fw-semibold font-dark1 fs-6">Confirm Password</label>
                <input
                  type="password"
                  style={bgTransparent}
                  placeholder="Confirm password"
                  autoComplete="new-password"
                  {...profileFormik.getFieldProps('confirmPassword')}
                  className={clsx(
                    'form-control bg-transparent',
                    {
                      'border-danger': profileFormik.touched.confirmPassword && profileFormik.errors.confirmPassword,
                    },
                    {
                      'border-success': profileFormik.touched.confirmPassword && !profileFormik.errors.confirmPassword,
                    }
                  )}
                />
                {profileFormik.touched.confirmPassword && profileFormik.errors.confirmPassword && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{profileFormik.errors.confirmPassword}</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="fv-row mb-8">
                <label className="form-check form-check-inline" htmlFor="kt_signup_terms_agree">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="kt_signup_terms_agree"
                    {...profileFormik.getFieldProps('acceptTerms')}
                  />
                  <span className="font-dark1">
                    I agree to the{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                  </span>
                </label>
                {profileFormik.touched.acceptTerms && profileFormik.errors.acceptTerms && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{profileFormik.errors.acceptTerms}</span>
                    </div>
                  </div>
                )}
              </div>
              
              <Button
                type="submit"
                className="btn btn-primary w-100 mb-3"
                disabled={loading || !profileFormik.isValid}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
          </div>
        )

      case SignupStep.SUCCESS:
        return (
          <div className="text-center">
            <div className="mb-4">
              <i className="fas fa-check-circle text-success" style={{ fontSize: '64px' }}></i>
            </div>
            <h1 className="text-dark fw-bolder mb-3">Welcome to Skanteq!</h1>
            <p className="font-dark1 mb-8">
              Your account has been created successfully.<br />
              Redirecting you to the dashboard...
            </p>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="d-flex flex-column flex-center min-vh-100">
      <div className="card shadow-sm" style={{ width: '100%', maxWidth: '500px' }}>
        <div className="card-body p-8">
          {/* Progress indicator */}
          <div className="d-flex justify-content-center mb-8">
            <div className="d-flex align-items-center">
              {[SignupStep.EMAIL, SignupStep.VERIFY_CODE, SignupStep.COMPLETE_PROFILE].map((step, index) => (
                <React.Fragment key={step}>
                  <div
                    className={clsx(
                      'rounded-circle d-flex align-items-center justify-content-center',
                      {
                        'bg-primary text-white': currentStep === step || 
                          ([SignupStep.VERIFY_CODE, SignupStep.COMPLETE_PROFILE, SignupStep.SUCCESS].includes(currentStep) && step === SignupStep.EMAIL) ||
                          ([SignupStep.COMPLETE_PROFILE, SignupStep.SUCCESS].includes(currentStep) && step === SignupStep.VERIFY_CODE),
                        'bg-light text-muted': currentStep !== step && 
                          !([SignupStep.VERIFY_CODE, SignupStep.COMPLETE_PROFILE, SignupStep.SUCCESS].includes(currentStep) && step === SignupStep.EMAIL) &&
                          !([SignupStep.COMPLETE_PROFILE, SignupStep.SUCCESS].includes(currentStep) && step === SignupStep.VERIFY_CODE)
                      }
                    )}
                    style={{ width: '32px', height: '32px', fontSize: '14px' }}
                  >
                    {index + 1}
                  </div>
                  {index < 2 && (
                    <div
                      className={clsx('mx-3', {
                        'bg-primary': ([SignupStep.VERIFY_CODE, SignupStep.COMPLETE_PROFILE, SignupStep.SUCCESS].includes(currentStep) && index === 0) ||
                          ([SignupStep.COMPLETE_PROFILE, SignupStep.SUCCESS].includes(currentStep) && index === 1),
                        'bg-light': !([SignupStep.VERIFY_CODE, SignupStep.COMPLETE_PROFILE, SignupStep.SUCCESS].includes(currentStep) && index === 0) &&
                          !([SignupStep.COMPLETE_PROFILE, SignupStep.SUCCESS].includes(currentStep) && index === 1)
                      })}
                      style={{ width: '40px', height: '2px' }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {renderStep()}
          
          {currentStep !== SignupStep.SUCCESS && (
            <div className="text-center mt-4">
              <span className="text-muted">Already have an account? </span>
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => navigate('/auth/login')}
                disabled={loading}
              >
                Sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MultiStepSignup 